import { useEffect, useState, FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { adminApi } from '../api/adminApi';
import { EmailCollisionModal } from '../components/admin/EmailCollisionModal';
import { usePageTitle } from '../hooks/usePageTitle';
import type { CreateUserRequest, FeatureCatalogItem } from '../types/admin';

type RoleOption = 'Public User' | 'Editor';

export function CreateUserPage() {
  const navigate = useNavigate();

  usePageTitle('Create User — Hawkes Bridge');

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [temporaryPassword, setTemporaryPassword] = useState('');
  const [role, setRole] = useState<RoleOption>('Public User');
  const [catalog, setCatalog] = useState<FeatureCatalogItem[]>([]);
  const [selectedCodes, setSelectedCodes] = useState<Set<string>>(new Set());
  const [submitting, setSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [collision, setCollision] = useState<{ existingUserId: number; email: string } | null>(null);

  useEffect(() => {
    adminApi.getCatalog().then(setCatalog).catch(() => setErrorMessage('Failed to load feature catalog.'));
  }, []);

  const toggleCode = (code: string) => {
    setSelectedCodes(prev => {
      const next = new Set(prev);
      if (next.has(code)) next.delete(code); else next.add(code);
      return next;
    });
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setErrorMessage(null);
    setSubmitting(true);

    const request: CreateUserRequest = {
      firstName: firstName.trim(),
      lastName: lastName.trim(),
      email: email.trim(),
      temporaryPassword,
      role,
      featureCodes: role === 'Editor' ? Array.from(selectedCodes) : [],
    };

    try {
      const created = await adminApi.createUser(request);
      // Land on the User Management page with the new row highlighted.
      navigate(`/admin/users?highlight=${created.id}`);
    } catch (err) {
      if (axios.isAxiosError(err) && err.response?.status === 409) {
        const body = err.response.data as { existingUserId?: number; code?: string };
        if (body.code === 'EMAIL_EXISTS' && typeof body.existingUserId === 'number') {
          setCollision({ existingUserId: body.existingUserId, email: request.email });
          return;
        }
      }
      if (axios.isAxiosError(err) && err.response?.status === 400) {
        const body = err.response.data as { code?: string; message?: string };
        setErrorMessage(body.message ?? 'Validation failed.');
        return;
      }
      setErrorMessage('Unexpected error creating the account.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <h1 className="text-2xl font-bold text-ink mb-6">Create User</h1>

      <form onSubmit={handleSubmit} className="space-y-5 bg-card rounded-lg shadow p-6 border border-line">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label htmlFor="firstName" className="block text-sm font-medium text-ink">First name</label>
            <input
              id="firstName"
              type="text"
              required
              value={firstName}
              onChange={e => setFirstName(e.target.value)}
              className="mt-1 block w-full rounded-md border-line-strong shadow-sm focus:border-primary-500 focus:ring-primary-500"
            />
          </div>
          <div>
            <label htmlFor="lastName" className="block text-sm font-medium text-ink">Last name</label>
            <input
              id="lastName"
              type="text"
              required
              value={lastName}
              onChange={e => setLastName(e.target.value)}
              className="mt-1 block w-full rounded-md border-line-strong shadow-sm focus:border-primary-500 focus:ring-primary-500"
            />
          </div>
        </div>

        <div>
          <label htmlFor="email" className="block text-sm font-medium text-ink">Email</label>
          <input
            id="email"
            type="email"
            required
            value={email}
            onChange={e => setEmail(e.target.value)}
            className="mt-1 block w-full rounded-md border-line-strong shadow-sm focus:border-primary-500 focus:ring-primary-500"
          />
        </div>

        <div>
          <label htmlFor="temporaryPassword" className="block text-sm font-medium text-ink">
            Temporary password
          </label>
          <input
            id="temporaryPassword"
            type="password"
            autoComplete="new-password"
            required
            value={temporaryPassword}
            onChange={e => setTemporaryPassword(e.target.value)}
            className="mt-1 block w-full rounded-md border-line-strong shadow-sm focus:border-primary-500 focus:ring-primary-500 font-mono"
          />
          <p className="mt-1 text-xs text-ink-soft">
            Share this out-of-band with the new user. They will be forced to change it on first login.
          </p>
        </div>

        <div>
          <label htmlFor="role" className="block text-sm font-medium text-ink">Role</label>
          <select
            id="role"
            value={role}
            onChange={e => {
              const next = e.target.value as RoleOption;
              setRole(next);
              if (next !== 'Editor') setSelectedCodes(new Set());
            }}
            className="mt-1 block w-full rounded-md border-line-strong shadow-sm focus:border-primary-500 focus:ring-primary-500"
          >
            <option>Public User</option>
            <option>Editor</option>
          </select>
        </div>

        {role === 'Editor' && (
          <fieldset className="border border-line rounded-md p-4">
            <legend className="px-2 text-sm font-medium text-ink">Feature grants</legend>
            <div className="space-y-2">
              {catalog.filter(f => f.isActive).map(feature => (
                <label key={feature.code} className="flex items-start space-x-3">
                  <input
                    type="checkbox"
                    checked={selectedCodes.has(feature.code)}
                    onChange={() => toggleCode(feature.code)}
                    className="mt-1 rounded border-line-strong text-primary-600 focus:ring-primary-500"
                  />
                  <span>
                    <span className="block text-sm font-medium text-ink">{feature.displayName}</span>
                    {feature.description && (
                      <span className="block text-xs text-ink-soft">{feature.description}</span>
                    )}
                  </span>
                </label>
              ))}
            </div>
          </fieldset>
        )}

        {errorMessage && (
          <div role="alert" className="rounded-md bg-red-50 border border-red-200 p-3 text-sm text-red-800">
            {errorMessage}
          </div>
        )}

        <div className="flex justify-end space-x-3">
          <button
            type="button"
            onClick={() => navigate('/admin')}
            className="px-4 py-2 text-sm font-medium text-ink bg-card border border-line-strong rounded-md hover:bg-cream"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={submitting}
            className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-md hover:bg-primary-700 disabled:opacity-60"
          >
            {submitting ? 'Creating…' : 'Create user'}
          </button>
        </div>
      </form>

      {collision && (
        <EmailCollisionModal
          existingUserId={collision.existingUserId}
          email={collision.email}
          onClose={() => setCollision(null)}
        />
      )}
    </div>
  );
}
